import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Github, GitlabIcon, Cloud, Star, Lock, Globe, Search, Settings2, Loader2 } from "lucide-react";

const PROVIDER_ICONS = { github: Github, gitlab: GitlabIcon, azure: Cloud };
function ProviderIcon({ id, ...props }) {
  const Icon = PROVIDER_ICONS[id] || Cloud;
  return <Icon {...props} />;
}

export default function Repositories() {
  const navigate = useNavigate();
  const [conns, setConns] = useState([]);
  const [provider, setProvider] = useState(null);
  const [repos, setRepos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/connections").then((r) => {
      setConns(r.data);
      if (r.data.length) setProvider(r.data[0].provider);
    });
  }, []);

  useEffect(() => {
    if (!provider) return;
    setLoading(true);
    setRepos([]);
    api.get(`/repos?provider=${provider}`)
      .then((r) => setRepos(r.data.repos))
      .catch((err) => toast.error(formatError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [provider]);

  const filtered = repos.filter((r) => r.full_name.toLowerCase().includes(q.toLowerCase()));

  if (conns.length === 0) {
    return (
      <div className="p-8">
        <h1 className="text-4xl font-black tracking-tighter mb-8">Repositórios</h1>
        <div className="border border-dashed border-line p-10 text-center text-zinc-500">
          &gt; conecte um provedor primeiro_
          <div className="mt-4">
            <button onClick={() => navigate("/app/connections")} data-testid="go-connections"
              className="bg-acid text-black font-bold uppercase tracking-widest text-sm px-6 py-3 hover:bg-[#32e612]">
              Ir para conexões
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 fadeup">
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-1">Seleção</div>
      <h1 className="text-4xl font-black tracking-tighter mb-6">Repositórios</h1>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex gap-2">
          {conns.map((c) => (
            <button key={c.provider} data-testid={`repo-provider-${c.provider}`} onClick={() => setProvider(c.provider)}
              className={`flex items-center gap-2 border px-4 py-2 text-sm uppercase tracking-widest transition-colors ${provider === c.provider ? "border-acid text-acid bg-acid/5" : "border-line text-zinc-400 hover:border-zinc-500"}`}>
              <ProviderIcon id={c.provider} size={16} /> {c.provider}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 border border-line bg-void px-3 flex-1 min-w-[200px]">
          <Search size={16} className="text-zinc-500" />
          <input data-testid="repo-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="filtrar repositórios..."
            className="bg-transparent py-2 text-sm focus:outline-none w-full" />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-zinc-500 py-16 justify-center"><Loader2 className="animate-spin text-acid" /> carregando repositórios...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((r) => (
            <div key={`${r.provider}-${r.external_id}`} data-testid={`repo-card-${r.name}`}
              className="border border-line bg-surface p-5 flex flex-col hover:-translate-y-1 hover:shadow-[4px_4px_0px_#39FF14] transition-all duration-150">
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-bold text-base tracking-tight truncate pr-2">{r.name}</h3>
                {r.private ? <Lock size={14} className="text-zinc-500 shrink-0" /> : <Globe size={14} className="text-zinc-500 shrink-0" />}
              </div>
              <div className="text-xs text-zinc-500 truncate mb-2">{r.full_name}</div>
              <p className="text-xs text-zinc-400 line-clamp-2 flex-1 mb-3 min-h-[32px]">{r.description || "—"}</p>
              <div className="flex items-center gap-3 text-xs text-zinc-500 mb-4">
                {r.language && <span className="border border-line px-2 py-0.5">{r.language}</span>}
                <span className="flex items-center gap-1"><Star size={12} /> {r.stars}</span>
                <span className="ml-auto">{r.default_branch}</span>
              </div>
              <button data-testid={`configure-${r.name}`}
                onClick={() => navigate("/app/build", { state: { repo: r } })}
                className="w-full bg-acid text-black font-bold uppercase tracking-widest text-xs py-2.5 flex items-center justify-center gap-2 hover:bg-[#32e612] transition-colors">
                <Settings2 size={14} /> Configurar pipeline
              </button>
            </div>
          ))}
          {filtered.length === 0 && <div className="text-zinc-500 col-span-full py-10 text-center">&gt; nenhum repositório_</div>}
        </div>
      )}
    </div>
  );
}
